'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { onAuthStateChanged, onIdTokenChanged, User as FirebaseAuthUser, type Auth } from 'firebase/auth';
import type { ProfileData, UserRole } from '@/types/profile';
import { useFirebase } from '@/firebase/provider';
// Server-only Firebase services must not be imported into client code.
// Use the client-facing API route `/api/user` instead of importing server helpers.

interface UserContextState {
  user: ProfileData | null; // The user object is now the unified ProfileData
  firebaseUser: FirebaseAuthUser | null;
  isLoading: boolean;
  error: Error | null;
  refetchUser: () => void;
  /**
   * Apply a known-good field change to the cached user immediately, without a
   * round trip.
   *
   * Used after a server action has already persisted a value the owner is
   * waiting on. Calling refetchUser() for a field we just saved flips isLoading,
   * re-renders every consumer of the context, and costs a full profile fetch,
   * which made saving a single yes/no answer feel like a page load. A fetch can
   * still be issued afterwards to pick up anything else the server changed.
   */
  patchUser: (updates: Partial<ProfileData>) => void;
}

const UserContext = createContext<UserContextState | undefined>(undefined);

const ADMIN_EMAILS = new Set([
  "ovaldaze@gmail.com",
  "stiankruger@gmail.com"
]);

function addAdminRole(user: ProfileData): ProfileData {
  if (user.email && ADMIN_EMAILS.has(user.email.toLowerCase())) {
    const hasAdminRole = user.roles?.some((role: UserRole) => role.role === 'Administrator');
    
    if (!hasAdminRole) {
      const adminRole: UserRole = {
        role: 'Administrator',
        assignedBy: 'system-override',
        dateAssigned: new Date().toISOString(),
      };
      
      const updatedUser: ProfileData = {
        ...user,
        roles: user.roles ? [...user.roles, adminRole] : [adminRole]
      };
      return updatedUser;
    }
  }
  return user;
}

function setServerAuthCookie(token: string | null) {
  if (typeof document === 'undefined') return;
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const secureFlag = isSecure ? '; Secure' : '';

  if (!token) {
    document.cookie = `__session=; Path=/; Max-Age=0; SameSite=Lax${secureFlag}`;
    return;
  }

  // Keep cookie lifetime aligned with Firebase ID token validity (~1 hour).
  document.cookie = `__session=${encodeURIComponent(token)}; Path=/; Max-Age=3600; SameSite=Lax${secureFlag}`;
}

export function UserProvider({ children }: { children: ReactNode }) {
  let firebaseError: Error | null = null;
  let auth: Auth | null = null;
  try {
    const firebase = useFirebase();
    auth = firebase.auth;
  } catch (e) {
    firebaseError = e instanceof Error ? e : new Error(String(e));
    console.error('UserProvider: useFirebase() failed. This means UserProvider is not wrapped in FirebaseProvider.', firebaseError);
  }
  const [firebaseUser, setFirebaseUser] = useState<FirebaseAuthUser | null>(null);
  const [user, setUser] = useState<ProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  if (firebaseError) {
    return <div style={{color: 'red', padding: 24}}>
      <b>FATAL: UserProvider is not wrapped in FirebaseProvider!</b>
      <pre>{firebaseError.message}</pre>
    </div>;
  }

  const fetchFullUser = useCallback(async (fbUser: FirebaseAuthUser) => {
    try {
      // no-store: the main load AND refetchUser() both come through here.
      // A cached /api/user response would make saved state (e.g. channel
      // connectivity) look sticky until a hard reload.
      const resp = await fetch(`/api/user?userId=${encodeURIComponent(fbUser.uid)}`, {
        cache: 'no-store',
      });
      if (resp.status === 404) {
        setError(null);
        return;
      }
      if (!resp.ok) {
        throw new Error(`Failed to fetch profile: ${resp.status}`);
      }
      const profileData = await resp.json().catch(() => null);

      if (profileData && !profileData.error) {
        setUser(currentUser => {
          if (!currentUser) return null;
          const mergedData = { ...currentUser, ...profileData };
          const finalUserData = addAdminRole(mergedData);
          return finalUserData;
        });
      }
    } catch (e: any) {
      console.error("UserContext: A critical error occurred while fetching the full user profile:", e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    if (!auth) return;

    let lastFbUserTs = 0;
    const IGNORE_WINDOW_MS = 30000;

    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      const now = Date.now();
      if (!fbUser && lastFbUserTs && now - lastFbUserTs < IGNORE_WINDOW_MS) {
        return;
      }
      if (fbUser) {
        lastFbUserTs = now;
      }

      if (fbUser) {
        fbUser.getIdToken().then((token) => {
          setServerAuthCookie(token);
        }).catch(() => {});
      } else {
        setServerAuthCookie(null);
      }

      setFirebaseUser(fbUser);
      if (fbUser) {
        setIsLoading(true);
        setError(null);
        const isPhoneUser = fbUser.providerData?.some((p) => p?.providerId === 'phone');
        const minimalUser: ProfileData = {
          id: fbUser.uid,
          userId: fbUser.uid,
          email: fbUser.email || '',
          username: fbUser.displayName || fbUser.email || 'New User',
          emailVerified: fbUser.emailVerified || isPhoneUser || false,
          authMethod: 'firebase',
        } as ProfileData;
        const userWithRole = addAdminRole(minimalUser);
        setUser(userWithRole);
        fetchFullUser(fbUser);
      } else {
        // --- AGENT SESSION FALLBACK ---
        // If no Firebase user, check for agent session (only on pages that need it).
        // Skip this check on public QA/search pages to avoid unnecessary 401 noise.
        const isPublicPage = typeof window !== 'undefined' && (
          window.location.pathname.startsWith('/search-profiles') ||
          window.location.pathname.startsWith('/qa/')
        );
        
        if (!isPublicPage) {
          (async () => {
            setIsLoading(true);
            try {
              const resp = await fetch('/api/agent/auth/me');
              if (resp.ok) {
                const agent = await resp.json().catch(() => null);
                if (agent && agent.agentNumber) {
                  setUser({
                    id: agent.agentNumber,
                    userId: agent.agentNumber,
                    username: agent.displayName || agent.agentNumber,
                    email: agent.email || '',
                    agentNumber: agent.agentNumber,
                    referralCode: agent.referralCode,
                    roles: [{ role: 'Agent', assignedBy: 'system', dateAssigned: new Date().toISOString() }],
                    authMethod: 'agent-cookie',
                    // Add any other fields needed for your app
                  });
                } else {
                  setUser(null);
                }
              } else {
                setUser(null);
              }
            } catch (e) {
              setUser(null);
            } finally {
              setIsLoading(false);
            }
          })();
        } else {
          // On public pages, skip agent session check to avoid unnecessary 401 errors
          setIsLoading(false);
        }
        setError(null);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('sessionId');
        }
      }
    });

    const unsubscribeIdToken = onIdTokenChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setServerAuthCookie(null);
        return;
      }
      try {
        const token = await fbUser.getIdToken();
        setServerAuthCookie(token);
      } catch {}
    });

    return () => {
      unsubscribe();
      unsubscribeIdToken();
    };
  }, [auth, fetchFullUser]);
  
  const refetchUser = useCallback(() => {
    if (auth?.currentUser) {
      setIsLoading(true);
      fetchFullUser(auth.currentUser).finally(() => setIsLoading(false));
    }
  }, [auth, fetchFullUser]);

  const patchUser = useCallback((updates: Partial<ProfileData>) => {
    setUser((currentUser) => {
      if (!currentUser) return currentUser;
      return addAdminRole({ ...currentUser, ...updates });
    });
  }, []);

  return (
    <UserContext.Provider value={{ user, firebaseUser, isLoading, error, refetchUser, patchUser }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
